/**
 * TAB COMPLETION — model-backed ghost text at the cursor.
 *
 * Pause typing and the host's provider is asked what comes next; the answer
 * is drawn after the cursor in a muted style. Tab accepts all of it,
 * Mod-ArrowRight accepts the next word, Escape dismisses it, and any edit or
 * cursor move that leaves the suggestion's position drops it.
 *
 * The kernel never talks to a model. A host supplies an `InlineCompletionProvider`
 * (desktop: an IPC call to the completer in core). Every request carries an
 * AbortSignal and is aborted by the next keystroke, so a slow answer for an
 * old prefix is never drawn over newer text. A provider that throws or returns
 * nothing shows nothing — completion is an accelerator, never an error.
 */
import { type Extension, Prec, StateEffect, StateField, type Transaction } from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView, type PluginValue, ViewPlugin, type ViewUpdate, WidgetType, keymap } from "@codemirror/view";

/** What a provider is asked: the text either side of the cursor. */
export interface InlineCompletionQuery {
  readonly path: string;
  /** Text before the cursor, bounded to `GHOST_PREFIX_CHARS`. */
  readonly prefix: string;
  /** Text after the cursor, bounded to `GHOST_SUFFIX_CHARS`. */
  readonly suffix: string;
  readonly signal: AbortSignal;
}

export interface InlineCompletionProvider {
  complete(query: InlineCompletionQuery): Promise<string | null>;
}

export const GHOST_DEBOUNCE_MS = 250;
export const GHOST_PREFIX_CHARS = 6_000;
export const GHOST_SUFFIX_CHARS = 2_000;
/** A suggestion is at most a short block; a provider may not dump a file into the buffer. */
export const GHOST_MAX_CHARS = 1_500;

interface Ghost {
  readonly pos: number;
  readonly text: string;
}

const setGhost = StateEffect.define<Ghost | null>();

class GhostWidget extends WidgetType {
  constructor(readonly text: string) {
    super();
  }
  override eq(other: GhostWidget): boolean {
    return other.text === this.text;
  }
  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "cm-ghost-text";
    span.setAttribute("aria-hidden", "true");
    // textContent only: provider output is untrusted text, never markup.
    span.textContent = this.text;
    return span;
  }
  override ignoreEvent(): boolean {
    return true;
  }
}

const ghostField = StateField.define<Ghost | null>({
  create: () => null,
  update(value: Ghost | null, tr: Transaction): Ghost | null {
    for (const e of tr.effects) if (e.is(setGhost)) return e.value;
    if (!value) return null;
    // Any edit drops the suggestion (it was computed for the old text), and so
    // does a cursor that is no longer exactly where it was offered.
    if (tr.docChanged) return null;
    const sel = tr.state.selection.main;
    if (!sel.empty || sel.head !== value.pos) return null;
    return value;
  },
  provide: (f) =>
    EditorView.decorations.from(f, (g): DecorationSet =>
      g ? Decoration.set([Decoration.widget({ widget: new GhostWidget(g.text), side: 1 }).range(g.pos)]) : Decoration.none,
    ),
});

/** The suggestion currently shown, if any (for hosts and tests). */
export function currentGhost(view: EditorView): { pos: number; text: string } | null {
  return view.state.field(ghostField, false) ?? null;
}

/** Clean a provider's answer: bounded, no trailing whitespace-only tail, never empty. */
export function normalizeGhost(text: string | null): string | null {
  if (typeof text !== "string") return null;
  let t = text.length > GHOST_MAX_CHARS ? text.slice(0, GHOST_MAX_CHARS) : text;
  t = t.replace(/\s+$/u, (tail) => (tail.includes("\n") ? "" : tail));
  return t.trim().length === 0 ? null : t;
}

/** The next word of a suggestion: leading whitespace plus one run of word or non-word characters. */
export function nextWord(text: string): string {
  const m = /^\s*(?:[\p{L}\p{N}_$]+|[^\s\p{L}\p{N}_$]+)/u.exec(text);
  return m ? m[0] : text;
}

function accept(view: EditorView, part: "all" | "word"): boolean {
  const g = view.state.field(ghostField, false);
  if (!g) return false;
  const insert = part === "all" ? g.text : nextWord(g.text);
  const rest = g.text.slice(insert.length);
  const pos = g.pos + insert.length;
  view.dispatch({
    changes: { from: g.pos, insert },
    selection: { anchor: pos },
    userEvent: "input.complete",
  });
  // Accepting a word keeps the rest on offer from the new position.
  if (rest.length > 0 && part === "word") view.dispatch({ effects: setGhost.of({ pos, text: rest }) });
  return true;
}

class GhostPlugin implements PluginValue {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight: AbortController | null = null;

  constructor(
    private readonly view: EditorView,
    private readonly provider: InlineCompletionProvider,
    private readonly pathOf: () => string,
    private readonly debounceMs: number,
  ) {}

  update(update: ViewUpdate): void {
    // Only typing asks for a suggestion: an external document load, an undo
    // or an accepted completion does not.
    const typed = update.transactions.some((tr) => tr.isUserEvent("input.type") || tr.isUserEvent("delete"));
    if (update.docChanged || update.selectionSet) this.cancel();
    if (!typed) return;
    this.timer = setTimeout(() => void this.request(), this.debounceMs);
  }

  private cancel(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.inflight?.abort();
    this.inflight = null;
  }

  private async request(): Promise<void> {
    this.timer = null;
    const state = this.view.state;
    const sel = state.selection.main;
    if (!sel.empty || state.readOnly) return;
    const pos = sel.head;
    // Mid-word to the right means the person is editing inside a token, not
    // writing new code; a suggestion there only gets in the way.
    const after = state.sliceDoc(pos, Math.min(state.doc.length, pos + 1));
    if (/[\p{L}\p{N}_$]/u.test(after)) return;
    const controller = new AbortController();
    this.inflight = controller;
    const doc = state.doc;
    const query: InlineCompletionQuery = {
      path: this.pathOf(),
      prefix: doc.sliceString(Math.max(0, pos - GHOST_PREFIX_CHARS), pos),
      suffix: doc.sliceString(pos, Math.min(doc.length, pos + GHOST_SUFFIX_CHARS)),
      signal: controller.signal,
    };
    let text: string | null = null;
    try {
      text = normalizeGhost(await this.provider.complete(query));
    } catch {
      text = null;
    }
    if (controller.signal.aborted || this.inflight !== controller) return;
    this.inflight = null;
    // The document and cursor must be exactly as they were when we asked.
    if (this.view.state.doc !== doc || this.view.state.selection.main.head !== pos || !text) return;
    this.view.dispatch({ effects: setGhost.of({ pos, text }) });
  }

  destroy(): void {
    this.cancel();
  }
}

const GHOST_THEME = EditorView.baseTheme({
  ".cm-ghost-text": { opacity: "0.45", fontStyle: "italic", whiteSpace: "pre" },
});

/**
 * The ghost-text extension. Without a provider it is empty, so a host that
 * offers no model pays nothing. `debounceMs` is for tests.
 */
export function ghostTextExtension(provider: InlineCompletionProvider | undefined, pathOf: () => string, debounceMs: number = GHOST_DEBOUNCE_MS): Extension {
  if (!provider) return [];
  return [
    ghostField,
    ViewPlugin.define((view) => new GhostPlugin(view, provider, pathOf, debounceMs)),
    // Highest precedence: Tab accepts a visible suggestion before it can
    // indent; with nothing shown, every key falls through unchanged.
    Prec.highest(
      keymap.of([
        { key: "Tab", run: (view) => accept(view, "all") },
        { key: "Mod-ArrowRight", run: (view) => accept(view, "word") },
        {
          key: "Escape",
          run: (view) => {
            if (!view.state.field(ghostField, false)) return false;
            view.dispatch({ effects: setGhost.of(null) });
            return true;
          },
        },
      ]),
    ),
    GHOST_THEME,
  ];
}
